import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from "@angular/forms";
import {
  ApplicantAddress,
  ApplicantRequest, ApplicationLogDetailsRequest,
  ApplicationLogsControllerService,
  ApplicationLogsRequest, ApplicationLogStepRequest,
  ApplicationLogsUpdateRequest,
  Log,
  Step
} from "leia-api";
import {FacadeService} from "src/app/shared/service-facade/facade.service";
import {UtilitiesService, ValidArray} from "src/app/shared/services/utilities.service";
import {environment} from "src/environments/environment";
import {CustomValidators} from "src/app/shared/validators/custom-validator";
import {formatDate} from "@angular/common";
import {
  EmploymentDetailsEmptyData,
  EmploymentDetailsPrimaryDevData,
  EmploymentDetailsSecondaryDevData,
  TellUsAboutYourselfEmptyData,
  TellUsAboutYourselfPrimaryDevData,
  TellUsAboutYourselfSecondaryDevData
} from "src/app/shared/model/form-data";
import {SectionStatusService} from "src/app/status-tracker/section-status.service";
import {Sections, SectionStates} from "src/app/status-tracker/model/section-status";
import {CustomerControllerService} from "skyhopper-api";
import {FormState} from "src/app/core/interfaces/form-state";
import {OnboardingApplicant} from "src/app/shared/model/onboarding-applicant.model";
import {OnboardingRequest} from "src/app/shared/model/onboarding-request.model";
import {ApplicantType} from "../models/applicant-type";
import {Subject} from 'rxjs';
import {CoreService} from 'src/app/shared/services/core.service';
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import StatusEnum = ApplicationLogsUpdateRequest.StatusEnum;
import {BASK_BRAND_ID} from "../../shared/constants";
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingSourceEnum = ApplicationLogStepRequest.OnboardingSourceEnum;

//const mParticle = require("@mparticle/web-sdk");

const PRIMARY_APPLICANT_LABEL = "Primary Applicant";
const SECONDARY_APPLICANT_LABEL = "Co-Applicant";
const MINOR_APPLICANT_LABEL = "Minor Applicant";
const ADULT_APPLICANT_LABEL = "Adult Applicant";


@Injectable({
  providedIn: 'root'
})
export class ApplicantsService {
  duplicatedDataErrors$: Subject<ValidationErrors> = new Subject();

  private _isSecondaryApplicantMarkedForRemoval = false;
  private _isInJointMaintenanceFlow = false;

  constructor(
    private facadeService: FacadeService,
    private formBuilder: FormBuilder,
    private utils: UtilitiesService,
    private sectionStatusService: SectionStatusService,
    private customerControllerService: CustomerControllerService,
    private coreService: CoreService,
    private applicationLogService: ApplicationLogsControllerService,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this._isInJointMaintenanceFlow = this.facadeService.isInJointMaintenanceFlow();
  }

  sendDuplicatedDataErrors(errors: ValidationErrors): void {
    this.duplicatedDataErrors$.next(errors);
  }

  createTellUsAboutYourselfFormGroup(isPrimary: boolean, isMinor: boolean): Promise<FormGroup> {
    const applicationLogRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();

    //Set the application is joint or single by checking the primary/minor flag. 0-Single, 1 - Joint
    if(!isPrimary || isMinor) applicationLogRequest.isJoint = 1;
    return new Promise(resolve => {
      this.facadeService.createNewApplicant().subscribe(id => {
        if (!Array.isArray(applicationLogRequest.applicants)) {
          applicationLogRequest.applicants = [];
        }

        if (!Array.isArray(onboardingRequest.applicants)) {
          onboardingRequest.applicants = [];
        }

        applicationLogRequest.applicants.push({applicantId: id, primary: isPrimary});

        const onboardingApplicant = new OnboardingApplicant();
        onboardingApplicant.applicantId = id;
        onboardingApplicant.applicantType = isPrimary ? ApplicantType.Primary : ApplicantType.Secondary;
        onboardingApplicant.isMinor = isMinor;
        onboardingRequest.applicants.push(onboardingApplicant);

        this.facadeService.setShoppingCartApplicationLogsRequest(applicationLogRequest);
        this.facadeService.setShoppingCartOnboardingRequest(onboardingRequest);

        var {
          firstName,
          middleName,
          lastName,
          suffix,
          preferredName,
          birthDate,
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode,
          phoneNumber,
          ssnNumber,
          email,
          isUSCitizen,
          citizenship
        } = TellUsAboutYourselfEmptyData;

        if (environment.development && isPrimary) {
          var {
            firstName,
            middleName,
            lastName,
            suffix,
            preferredName,
            birthDate,
            addressLine1,
            addressLine2,
            city,
            state,
            zipCode,
            phoneNumber,
            ssnNumber,
            email,
            isUSCitizen,
            citizenship
          } = TellUsAboutYourselfPrimaryDevData;
        }

        if (environment.development && !isPrimary) {
          var {
            firstName,
            middleName,
            lastName,
            suffix,
            preferredName,
            birthDate,
            addressLine1,
            addressLine2,
            city,
            state,
            zipCode,
            phoneNumber,
            ssnNumber,
            email,
            isUSCitizen,
            citizenship
          } = TellUsAboutYourselfSecondaryDevData;
        }

        let isEmployeeProduct = false;
        if(this.facadeService.getIsEmployeeProduct()) {
          isEmployeeProduct = true;
        }

        const form = this._createTellUsAboutYourselfFormGroup(
          id, isPrimary, isMinor, firstName, middleName, lastName, suffix, preferredName, ssnNumber, birthDate, addressLine1, addressLine2,
          city, state, zipCode, phoneNumber, email, isUSCitizen, citizenship, isEmployeeProduct
        );

        resolve(form);
      }, error => {
        let log = this.utils.createLog("BB8", error.toString());
        this.facadeService.handleInternalError(NameEnum.TellUsAboutYourself, StatusEnum.Rejected, [log]);
      });
    });
  }

  loadTellUsAboutYourselfFormGroup(applicant: ApplicantRequest, isMinor : boolean, isEmployeeProduct: boolean): FormGroup {
    const {
      applicantId, primary, firstName, middleName, lastName, suffix, nickname, ssn, dateOfBirth,
      phoneNumber, email
    } = applicant;
    const { streetAddress, streetAddress1, city, state, zip } = applicant.addresses![0];

    const form = this._createTellUsAboutYourselfFormGroup(
      applicantId!,
      primary!,
      isMinor,
      firstName!,
      middleName!,
      lastName!,
      suffix!,
      nickname!,
      ssn!,
      UtilitiesService.addTimeToDateString(dateOfBirth!),
      streetAddress!,
      streetAddress1!,
      city!,
      this.facadeService
        .getAllStates().find(data => data.stateAbbr === state)?.id || "Not Found",
      zip!,
      phoneNumber!,
      email!,
      (applicant as any).isUSCitizen!,
      (applicant as any).citizenship!,
      isEmployeeProduct
    );

    return form;
  }

  private _createDateObject(humanReadableDate: Date | string): Date | string {
    if (!!!humanReadableDate) {
      return "";
    }

    if (typeof(humanReadableDate) === "object") {
      return humanReadableDate;
    }

    return new Date(humanReadableDate);
  }

  private _createTellUsAboutYourselfFormGroup(
    id: number, isPrimary: boolean, isMinor : boolean, firstName: string, middleName: string, lastName: string, suffix: string, preferredName: string,
    ssnNumber: string, birthDate: string, addressLine1: string, addressLine2: string, city: string, state: any, zipCode: string,
    phoneNumber: string, email: string, isUSCitizen: boolean | null, citizenship: string, isEmployeeProduct: boolean
  ): FormGroup {
      const shouldApplyCitizenshipValidation = !this.facadeService.isInAuthenticatedFlow();

      let emailAddressValidationArray : any[] = [Validators.email, Validators.minLength(3), Validators.maxLength(100)];
      if(!isMinor) emailAddressValidationArray.push(Validators.required);

      //This validation required for employee Product
      emailAddressValidationArray.push(CustomValidators.emailAddressValidator);

      let phoneNumberValidationArray : any[] = [Validators.minLength(9), Validators.maxLength(12), CustomValidators.PhoneNumberValidation];
      if(!isMinor) phoneNumberValidationArray.push(Validators.required);

      const birthDateObject = this._createDateObject(birthDate!);
      const form = this.formBuilder.group({
        id: id,
        isPrimary: [isPrimary],
        applicantType: [this.getApplicantTypeLabel(isPrimary, this.facadeService.getIsMinor())],
        firstName: [firstName, [Validators.required, Validators.minLength(1), Validators.maxLength(100),CustomValidators.applicantNameSpecialCharacterValidator]],
        middleName: [middleName, [Validators.maxLength(100), CustomValidators.applicantNameSpecialCharacterValidator]],
        lastName: [lastName, [Validators.required, Validators.minLength(1), Validators.maxLength(100),CustomValidators.applicantNameSpecialCharacterValidator]],
        suffix: [suffix, [Validators.maxLength(25)]],
        preferredName: [preferredName, [Validators.maxLength(50)]],
        ssnNumber: [ssnNumber, [Validators.required, CustomValidators.socialSecurityNumberValidator]],
        birthDate: [birthDateObject, [Validators.required, Validators.maxLength(7)]],
        addressLine1: [addressLine1, [Validators.required, Validators.minLength(2), Validators.maxLength(51), CustomValidators.POBoxValidator({hasPOBox: true}), CustomValidators.AddressLine1Validator]],
        addressLine2: [addressLine2, [Validators.maxLength(15), CustomValidators.POBoxValidator({hasPOBox: true})]],
        city: [city, [Validators.required, Validators.minLength(1), Validators.maxLength(24)]],
        state: [state, [Validators.required, Validators.minLength(0), Validators.pattern("^[1-9][0-9]*$")]],
        zipCode: [zipCode, [Validators.required, Validators.minLength(4), Validators.maxLength(10)]],
        phoneNumber: [phoneNumber, phoneNumberValidationArray],
        emailAddress: [email, emailAddressValidationArray],
        isUSCitizen: new FormControl(
          isUSCitizen,
          {
            validators: shouldApplyCitizenshipValidation ? [Validators.required] : [],
            updateOn: "change"
          }
        ),
        citizenship: [citizenship, [Validators.maxLength(56)]],

      }, {updateOn: "blur"});

      const usCitizenControl = form.controls['isUSCitizen'];
      const citizenshipControl = form.controls['citizenship'];

      const setCountryValidators = (isUSCitizen: boolean | null): void => {
        if (isUSCitizen === false) {
          citizenshipControl.setValidators([Validators.required, Validators.maxLength(56)]);
        } else {
          citizenshipControl.clearValidators();
          citizenshipControl.setErrors(null);
          citizenshipControl.setValue("", {emitEvent: false});
        }

        citizenshipControl.updateValueAndValidity({emitEvent: false});
      };

      if (shouldApplyCitizenshipValidation) {
        setCountryValidators(usCitizenControl.value);
        usCitizenControl.valueChanges.subscribe(setCountryValidators);
      } else {
        citizenshipControl.clearValidators();
        citizenshipControl.updateValueAndValidity({emitEvent: false});
      }

      return form;
  }

  markSecondaryApplicantForRemoval(): void {
    this._isSecondaryApplicantMarkedForRemoval = true;
  }

  unmarkSecondaryApplicantForRemoval(): void {
    this._isSecondaryApplicantMarkedForRemoval = false;
  }

  async removeSecondaryApplicantIfNecessary(formToRemoveFrom?: FormArray): Promise<void> {
    if (!this._isSecondaryApplicantMarkedForRemoval) { return; }

    const applicationLogRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();

    const applicants = applicationLogRequest.applicants!;

    if (applicants.length === 1) { return; }

    //const applicantId = applicants.find(applicant => !applicant.primary)!.applicantId;
    const applicantId = onboardingRequest.applicants.find(applicant => applicant.applicantType! === ApplicantType.Secondary)?.applicantId;
    applicationLogRequest.applicants?.splice(
      applicationLogRequest.applicants?.findIndex(applicant => applicant.applicantId === applicantId), 1
    );

    onboardingRequest.applicants.splice(
      onboardingRequest.applicants.findIndex(applicant => applicant.applicantId === applicantId), 1
    );

    //Set the flag as 0 (Single Application) , 0 - Single, 1 - Joint
    applicationLogRequest.isJoint = 0;
    await this.facadeService.deleteApplicant(
      this.facadeService.getSessionId(),
      applicantId!
    );

    const logEntry = [this.utils.createLog("BB8", "Tell us about yourself section removed Co-applicant.")];
    const logDetailsEntry = [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, "Tell us about yourself section removed Co-applicant.")];

    // Set log entry applicant id to primary since joint applicant was deleted
    const validApplicants = new ValidArray(onboardingRequest.applicants);
    const primaryApplicantId = validApplicants.first()?.applicantId;

    await this._updateApplication(
      NameEnum.TellUsAboutYourself, OnboardingStepEnum.TellUsAboutYourself,
      StateEnum.Success, OnboardingStateEnum.Success,
      logEntry, logDetailsEntry,
      applicationLogRequest,
      onboardingRequest,
      primaryApplicantId!
    )

    if (formToRemoveFrom && formToRemoveFrom.length > 1) {
      const formValues = (formToRemoveFrom.value as any[]);
      const applicantToDeleteIndex = formValues.findIndex(applicant => !applicant.tellUsAboutYourself.isPrimary);
      formToRemoveFrom.removeAt(applicantToDeleteIndex);
    }
  }

  private _getApplicantById(onboardingRequest: OnboardingRequest, id: Number): OnboardingApplicant | undefined {
    return onboardingRequest.applicants.find(applicant => applicant.applicantId === id);
  }

  createEmploymentDetailsFormGroup(id: number, isPrimary: boolean): FormGroup {
    var {
      annualIncome,
      employerCountry,
      incomeSource,
      occupation
    } = EmploymentDetailsEmptyData;

    if (environment.development && isPrimary) {
      var {
        annualIncome,
        employerCountry,
        incomeSource,
        occupation
      } = EmploymentDetailsPrimaryDevData;
    }

    if (environment.development && !isPrimary) {
      var {
        annualIncome,
        employerCountry,
        incomeSource,
        occupation
      } = EmploymentDetailsSecondaryDevData;
    }

    return this._createEmploymentDetailsFormGroup(
      id, isPrimary, incomeSource, occupation, employerCountry, annualIncome
    );
  }

  loadEmploymentDetailsFormGroup(applicant: ApplicantRequest): FormGroup {
    const {
      applicantId,
      primary,
      annualIncome,
      employerCountry,
      occupation,
      primarySourceOfFunds
    } = applicant;

    return this._createEmploymentDetailsFormGroup(
      applicantId!,
      primary!,
      Number(primarySourceOfFunds!),
      Number(occupation!),
      Number(employerCountry!),
      Number(annualIncome!)
    )
  }

  private _getEmploymentDetailsValidators(): EmploymentDetailsValidators {
    return {
      incomeSourceValidators: [Validators.required, Validators.min(1)],
      occupationValidators: [Validators.required, Validators.min(1)],
      employerCountryValidators: [Validators.required, Validators.min(1)],
      annualIncomeValidators: [Validators.required, Validators.min(1)]
    }
  }

  private _createEmploymentDetailsFormGroup(
    id: number, isPrimary: boolean, incomeSource: number, occupation: number,
    employerCountry: number, annualIncome: number
  ): FormGroup {
    let validators: EmploymentDetailsValidators = this._getEmploymentDetailsValidators();

    return this.formBuilder.group({
      id: id,
      isPrimary: [isPrimary],
      applicantType: [this.getApplicantTypeLabel(isPrimary, this.facadeService.getIsMinor())],
      incomeSource: [incomeSource, validators.incomeSourceValidators],
      occupation: [occupation, validators.occupationValidators],
      employerCountry: [employerCountry, validators.employerCountryValidators],
      annualIncome: [annualIncome, validators.annualIncomeValidators]
    });
  }

  async onValidTellUsAboutYourself(form: FormGroup): Promise<void> {
    if (form.status !== FormState.VALID) return;

    const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    const {
      id, isPrimary, firstName, middleName, lastName, suffix, preferredName, ssnNumber, birthDate,
      addressLine1, addressLine2, city, state, zipCode, phoneNumber, emailAddress, isUSCitizen, citizenship
    } = form.value;

    const isCitizenshipQuestionRequired = !this.facadeService.isInAuthenticatedFlow();
    if (isCitizenshipQuestionRequired && (isUSCitizen === null || isUSCitizen === undefined)) return;
    if (isCitizenshipQuestionRequired && isUSCitizen === false && !citizenship) return;

    let logMessage = `${firstName} ${lastName} (Primary Applicant), Tell us about yourself section is complete.`;
    const applicant = applicationRequest.applicants!.find(applicant => applicant.applicantId === id)!;

    if(applicant === undefined) return;

    applicant.primary = isPrimary;
    if(!!firstName) applicant.firstName = firstName.trim();
    if(!!middleName) applicant.middleName = middleName.trim();
    if(!!lastName) applicant.lastName = lastName.trim();
    applicant.suffix = suffix;
    applicant.nickname = preferredName;

    const ssn = ssnNumber.replace(/\-/gi, "");
    if(!!ssn) applicant.ssn = ssn;

    applicant.dateOfBirth = formatDate(birthDate, "yyyy-MM-dd", this.locale);

    const address = new ApplicantAddress();
    address.addressType = 1; // By default all addresses are physical, this is the value for it
    address.streetAddress = addressLine1;
    address.streetAddress1 = addressLine2;
    address.city = city;
    address.zip = zipCode;

    applicant.phoneNumber = phoneNumber.replace(/\-/gi, "");
    if(!!emailAddress) applicant.email = emailAddress;
    // Include citizenship fields in the application logs payload sent to /leia/application/logs.
    (applicant as any).isUSCitizen = isUSCitizen;
    (applicant as any).citizenship = isUSCitizen === false ? citizenship : "";
    address.state = this.facadeService.getAllStates()
      .find(data => data.id === state)?.stateAbbr || "Not Found";

    applicant.addresses = [address];

    const onboardingApplicant = onboardingRequest.applicants.find(applicant => applicant.applicantId === id)!;
    if(!!firstName) onboardingApplicant.tellUsAboutYourself.firstName = firstName.trim();
    if(!!middleName) onboardingApplicant.tellUsAboutYourself.middleName = middleName.trim();
    if(!!lastName) onboardingApplicant.tellUsAboutYourself.lastName = lastName.trim();
    onboardingApplicant.tellUsAboutYourself.suffix = suffix;
    onboardingApplicant.tellUsAboutYourself.preferredName = preferredName;
    if(!!ssnNumber) onboardingApplicant.tellUsAboutYourself.ssnNumber = ssnNumber;
    if(!!birthDate) onboardingApplicant.tellUsAboutYourself.birthDate = birthDate;
    if(!!addressLine1) onboardingApplicant.tellUsAboutYourself.addressLine1 = addressLine1;
    onboardingApplicant.tellUsAboutYourself.addressLine2 = addressLine2;
    if(!!city) onboardingApplicant.tellUsAboutYourself.city = city;
    if(!!zipCode) onboardingApplicant.tellUsAboutYourself.zipcode = zipCode;
    if(!!phoneNumber) onboardingApplicant.tellUsAboutYourself.phoneNumber = phoneNumber;
    if(!!emailAddress) onboardingApplicant.tellUsAboutYourself.email = emailAddress;
    if(!!isUSCitizen) onboardingApplicant.tellUsAboutYourself.isUSCitizen = isUSCitizen;
    if(!!citizenship) onboardingApplicant.tellUsAboutYourself.citizenship = citizenship;
    onboardingApplicant.tellUsAboutYourself.state = address.state;
    if(!!state) onboardingApplicant.tellUsAboutYourself.stateId = state;

    if(!applicant.primary) {
      logMessage = `${firstName} ${lastName} (Co-Applicant), Tell us about yourself section is complete.`;
    }

    const logEntry = [this.utils.createLog("BB8", logMessage)];
    const logDetailsEntry = [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, logMessage)];

    //comment mParticle, CDP scripts are live, remove the code later
    //const currentUser = mParticle.default.Identity.getCurrentUser();
    // if(currentUser) {
    //   currentUser.setUserAttribute("FirstName",firstName);
    //   currentUser.setUserAttribute("LastName",lastName);
    //   currentUser.setUserAttribute("DOB",formatDate(birthDate, "MM-dd", this.locale));
    //   currentUser.setUserAttribute("Zip",zipCode);
    //   currentUser.setUserAttribute("Email",emailAddress);
    // }

    //comment mParticle, CDP scripts are live, remove the code later
    // mParticle.default.logEvent(
    //   "Tell Us About Yourself Form",
    //   mParticle.default.EventType.Navigation,
    //   {
    //     "FirstName": firstName,
    //     "LastName": lastName,
    //     "DOB": formatDate(birthDate, "MM-dd", this.locale),
    //     "Zip": zipCode, "Email": emailAddress
    //   }
    // );

    const brandId = Number(this.facadeService.getStorageItem("brandId"));
    //comment mParticle, CDP scripts are live, remove the code later
    // if(brandId == BASK_BRAND_ID) {
    //   mParticle.default.logPageView(
    //     "Step 2 - Form Start",
    //     {page: window.location.toString()}
    //   );
    // }

    await this._updateApplication(
      NameEnum.TellUsAboutYourself, OnboardingStepEnum.TellUsAboutYourself,
      StateEnum.Success, OnboardingStateEnum.Success,
      logEntry, logDetailsEntry,
      applicationRequest,
      onboardingRequest,
      applicant.applicantId
    );
  }

  async onValidEmploymentDetails(form: FormGroup): Promise<void> {
    if (form.status !== FormState.VALID) {
      return;
    }

    const applicationRequest = this.facadeService.getShoppingCartApplicationLogsRequest();
    const onboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
    const {id, incomeSource, occupation, employerCountry, annualIncome} = form.value;

    const applicant = applicationRequest.applicants!.find(applicant => applicant.applicantId === id)!;

    applicant.primarySourceOfFunds = incomeSource;
    applicant.occupation = occupation;
    applicant.employerCountry = employerCountry;
    applicant.annualIncome = annualIncome;

    let logMessage = `${applicant.firstName} ${applicant.lastName} (Primary Applicant), Employment details section complete.`;

    const onboardingApplicant = onboardingRequest.applicants.find(applicant => applicant.applicantId === id)!;
    onboardingApplicant.employmentDetails.annualIncomeId = annualIncome;
    onboardingApplicant.employmentDetails.countryId = employerCountry;
    onboardingApplicant.employmentDetails.annualIncomeId = annualIncome;
    onboardingApplicant.employmentDetails.occupationId = occupation;
    if(!applicant.primary) {
      logMessage = `${applicant.firstName} ${applicant.lastName} (Co-Applicant), Employment details section complete.`;
    }

    const logEntry = [this.utils.createLog("BB8", logMessage)];
    const logDetailsEntry = [this.utils.createApplicationLogDetailsRequest(OnboardingSourceEnum.Bb8, logMessage)];

    await this._updateApplication(
      NameEnum.EmploymentDetails, OnboardingStepEnum.EmploymentDetails,
      StateEnum.Success, OnboardingStateEnum.Success,
      logEntry, logDetailsEntry,
      applicationRequest,
      onboardingRequest,
      applicant.applicantId!
    );
  }

  private _updateApplication(section: NameEnum, onboardingStepEnum: OnboardingStepEnum, state: Step.StateEnum, onboardingStateEnum: OnboardingStateEnum, logs: Log[], applicationLogDetails: ApplicationLogDetailsRequest[], applicationLogsRequest: ApplicationLogsRequest, onboardingRequest: OnboardingRequest, applicantId : any): Promise<void> {
    applicationLogsRequest.step = this.utils.createApplicantStep(section, applicantId, state, logs);
    applicationLogsRequest.applicationLogStepRequest = this.utils.createEntireApplicationLogStepRequest(onboardingStepEnum, onboardingStateEnum, OnboardingSourceEnum.Bb8, applicantId, undefined, applicationLogDetails);

    this.facadeService.setShoppingCartOnboardingRequest(onboardingRequest);
    this.facadeService.setShoppingCartApplicationLogsRequest(applicationLogsRequest);
    return this.facadeService.updateApplication();
  }

  private getApplicantTypeLabel(isPrimary : boolean, isMinor : boolean) : string {
    let applicantType = isPrimary ? PRIMARY_APPLICANT_LABEL : SECONDARY_APPLICANT_LABEL;
    if(this.facadeService.getIsMinor()) {
      if (isPrimary) applicantType = MINOR_APPLICANT_LABEL;
      else applicantType = ADULT_APPLICANT_LABEL;
    }
    return applicantType;
  }
}

interface EmploymentDetailsValidators {
    incomeSourceValidators: Validators[];
    occupationValidators: Validators[];
    employerCountryValidators: Validators[];
    annualIncomeValidators: Validators[];
}
